import RoomStore from './RoomStore';
import WallStore from './WallStore';
import ModelStore from './ModelStore';
import {
  EEntityType,
  EOperateMode,
  ESceneMode,
  EToolType,
  EFaceSide,
  IEdgeWallSideInfoWithRelative,
  IOperateInfo,
  IWallEdge,
  IEdgeInfo
} from '../types';
import editorEvent, { EditorEventName } from '../event/editorEvent';
import { isEqual } from 'lodash-es';
import {
  OrthographicCamera,
  PerspectiveCamera,
  Scene,
  SRGBColorSpace,
  WebGLRenderer,
  AmbientLight,
  Color,
  ShaderMaterial,
  SphereGeometry,
  Mesh,
  BackSide,
  Fog,
  Group,
  EquirectangularReflectionMapping,
  TextureLoader
} from 'three';
import { History } from '../history';
import { StructStore } from './StructStore';
import { EContainerType } from '../types/entity';
import EditorStateStore from './EditorStateStore';
import { ALL_VISIBLE_STATE, VisibilityStore } from './VisibilityStore';
import { EVisibleEntityType, IVisibleState } from '../types/visible';
import { FLAGS } from '@/flags';
import { ParamStore } from './ParamStore';
import SeparateWallStore from './SeparateWallStore';
import roomEnvImage from '@/assets/images/roomEnvironment.jpg';
import { WallOperation } from '@/editor/operations/WallOperation';
import { SeparateWallOperation } from '@/editor/operations/SeparateWallOperation';
import { RoomOperation } from '@/editor/operations/RoomOperation';
import { sceneBgColor } from '../constant/color';
import { VisibleOperation } from '../operations/VisibleOperation';
import { FurnitureOperation } from '../operations/FurnitureOperation';
import { FaceStore } from './FaceStore';
import { RegionStore } from './RegionStore';
import { FaceOperation } from '../operations/FaceOperation';
import { IMaterialInfo } from '../data/material/material';
import { RegionOperation } from '../operations/RegionOperation';
import { CompositeOperation } from '../operations/CompositeOperation';
import { WindowDoorOperation } from '../operations/WindowDoorOperation';
import { EFaceType } from '../entities/type';
import { StructOperation } from '../operations/StructOperation';
import { syncDistanceTypeToCenterType } from '../utils/window';
import { PointStore } from './PointStore';
import { getWallSideStrArr } from '../utils/room';
import { LineSeg2d } from '../math/LineSeg2d';
import { compareCoreAndOutline, Edge, IAreaResult, PathFinder } from '../model/polygon/Region';
import { IPoint2d } from '../math/IPoint2d';
import EdgeStore from './EdgeStore';
import { ESeparateWallType, SeparateWallData } from '../data/separateWall/SeparateWall';
import { EWallFaceLayout, IFaceData } from '../data/face/face';
import { edgeToWallEdge } from '../utils/edge';
import { MaterialStore } from './materialStore';
import { WallBaseData } from '../data/wall/wallBaseData';
import { genStrsHash } from '../utils/hash';
import { KeyboardStore } from './keyboardStore';
import { ModelGroupStore } from './ModelGroupStore';
import { ModelGroupOperation } from '../operations/ModelGroupOperation';
import { AssemblyOperation } from '../operations/AssemblyOperation';
import { MultiModelOperation } from '../operations/MultiModelOperation';
import { AssemblyStore } from './AssemblyStore';
import { ArcSeg2d } from '../math/arcSeg2d';
import { IShapeSeg2d } from '../types/shape';
import { WallData } from '../data/wall/Wall';
import { reverseShape } from '../utils/wallShape';
import { AlignOperation } from '../operations/AlignOperation';
import type { IMultiModelData } from '../data/multiModel/multiModel';
import type { IColumnData, IWindowData } from '../data/model/model';
import type { IFurniture } from '../data/model/furniture';
import type { RegionData } from '../data/region/Region';
import type { IModelGroupData } from '../data/modelGroup/modelGroup';
import type { IAssemblyData } from '../data/assembly/assembly';
import type { RoomData } from '../data/room/Room';
import type { IAreaDimension, ILinearDimension } from '../data/dimension/dimension';
import { getShapeSeg } from '../math/shape/other';
import { MeasureOperation } from '../operations/MeasureOperation';
import { DimensionStore } from './DimensionStore';
import { DimensionOperation } from '../operations/DimensionOperation';

/** 2/3D 模式切换应该保持选中的 entity 类型 */
const ENTITY_SHOULD_KEEP_SELECT_WHEN_SCENE_MODE_CHANGE: EEntityType[] = [
  EEntityType.Furniture,
  EEntityType.DoorWindow,
  EEntityType.Region,
  EEntityType.Room,
  EEntityType.Assembly,
  EEntityType.MultiModel
];

const MODES_ONLY_AVAILIABLE_IN_2D = [
  EOperateMode.DrawWall,
  EOperateMode.DrawRectWall,
  EOperateMode.DrawArcWall,
  EOperateMode.DrawDoorWindow,
  EOperateMode.DrawStruct,
  EOperateMode.DrawFurniture,
  EOperateMode.DrawSeparateWall,
  EOperateMode.DrawAreaDimension,
  EOperateMode.DrawArcSeparateWall,
  EOperateMode.Array,
  EOperateMode.DrawMultiModel,
  EOperateMode.Alignment,
  EOperateMode.Floorplan,
  EOperateMode.Layout,
  EOperateMode.MeasureRuler,
  EOperateMode.DrawLinearDimension,
  EOperateMode.DrawAreaDimension
];

const DEFAULT_DYNAMIC_ABSORB_DISTANCE = 200;

const normalizeZoom = (zoom: number) => {
  return Number.isFinite(zoom) && zoom > 0 ? zoom : 1;
};

interface IValidEntityTypeDataMap {
  [EEntityType.MultiModel]: IMultiModelData;
  [EEntityType.Wall]: WallData;
  [EEntityType.SeparateWall]: SeparateWallData;
  [EEntityType.Floor]: RoomData;
  [EEntityType.DoorWindow]: IWindowData;
  [EEntityType.Column]: IColumnData;
  [EEntityType.Furniture]: IFurniture;
  [EEntityType.Region]: RegionData;
  [EEntityType.Face]: IFaceData;
  [EEntityType.ModelGroup]: IModelGroupData;
  [EEntityType.Assembly]: IAssemblyData;
  [EEntityType.Room]: RoomData;
  [EEntityType.AreaDimension]: IAreaDimension;
  [EEntityType.LinearDimension]: ILinearDimension;
}

type TEntityTypeDataMap = IValidEntityTypeDataMap & {
  [K in Exclude<EEntityType, keyof IValidEntityTypeDataMap>]: undefined;
};

// 「标尺」是继承式开关，关掉它只影响墙体标尺，方案里已有的手绘尺寸标注照常显示
const INITIAL_VISIBLE_STATE: IVisibleState = FLAGS.FEATURES.ENABLE_VISIBLE_V1
  ? { ...ALL_VISIBLE_STATE, [EVisibleEntityType.Ruler]: false, [EEntityType.FloorPlan]: false }
  : ALL_VISIBLE_STATE;

class Stores {
  // 历史记录
  public history: History = new History(this);

  editorStateStore: EditorStateStore = new EditorStateStore();
  visibilityStore: VisibilityStore = new VisibilityStore(INITIAL_VISIBLE_STATE);

  pointStore: PointStore = new PointStore();
  edgeStore: EdgeStore = new EdgeStore();
  private _wallStore: WallStore = new WallStore(this);
  private _modelStore: ModelStore = new ModelStore(this);
  private _structStore: StructStore = new StructStore(this);
  private _materialStore: MaterialStore = new MaterialStore();
  private _modelGroupStore: ModelGroupStore = new ModelGroupStore(this);
  private _assemblyStore: AssemblyStore = new AssemblyStore(this);
  private _dimensionStore: DimensionStore = new DimensionStore(this);

  private _paramStore: ParamStore = new ParamStore(this);
  private _roomStore: RoomStore = new RoomStore(this);
  private _separateWallStore: SeparateWallStore = new SeparateWallStore(this);
  private _regionStore: RegionStore = new RegionStore(this);
  private _faceStore: FaceStore = new FaceStore(this);
  private _keyboardStore = new KeyboardStore(this);

  // 操作
  public wallOperation = new WallOperation(this);
  public separateWallOperation = new SeparateWallOperation(this);
  public roomOperation = new RoomOperation(this);
  public furnitureOperation = new FurnitureOperation(this);
  public modelGroupOperation = new ModelGroupOperation(this);
  public assemblyOperation = new AssemblyOperation(this);
  public multiModelOperation = new MultiModelOperation(this);
  public windowDoorOperation = new WindowDoorOperation(this);
  public visibleOperation = new VisibleOperation(this);
  public structOperation = new StructOperation(this);
  public faceOperation = new FaceOperation(this);
  public regionOperation = new RegionOperation(this);
  public compositeOperation = new CompositeOperation(this);
  public alignOperation = new AlignOperation(this);
  public measureOperation = new MeasureOperation(this);
  public dimensionOperation = new DimensionOperation(this);

  private _operateInfo: IOperateInfo = {
    mode: EOperateMode.EMPTY,
    toolType: EToolType.empty
  };
  private _prevOperateInfo: IOperateInfo = {
    mode: EOperateMode.EMPTY,
    toolType: EToolType.empty
  };

  private _scene2dZoom = 100;
  private _scene3dZoom = 100;
  private _dynamicAbsorbDistance = DEFAULT_DYNAMIC_ABSORB_DISTANCE;

  private _sceneMode: ESceneMode = ESceneMode.scene2d;

  connectedWallOutlines: {
    path: IPoint2d[];
    edges: IWallEdge[];
    outest: boolean; // 是否为最外层轮廓
  }[] = []; // 墙体外轮廓，用来渲染连接墙轮廓
  remainingEdges: IWallEdge[] = []; // 剩余的边(不在connectedWallOutlines中的边)

  constructor() {
    this.history.init();
  }

  get wallStore() {
    return this._wallStore;
  }

  get roomStore() {
    return this._roomStore;
  }

  get regionStore() {
    return this._regionStore;
  }

  get modelStore() {
    return this._modelStore;
  }

  get modelGroupStore() {
    return this._modelGroupStore;
  }

  get assemblyStore() {
    return this._assemblyStore;
  }

  get materialStore() {
    return this._materialStore;
  }

  get structStore() {
    return this._structStore;
  }

  get paramStore() {
    return this._paramStore;
  }

  get separateWallStore() {
    return this._separateWallStore;
  }

  get faceStore() {
    return this._faceStore;
  }

  get keyboardStore() {
    return this._keyboardStore;
  }

  get dimensionStore() {
    return this._dimensionStore;
  }

  get operateInfo() {
    return this._operateInfo;
  }

  get currentSceneZoom() {
    return (this._sceneMode === ESceneMode.scene3d ? this._scene3dZoom : this._scene2dZoom) / 100;
  }

  get dynamicAbsorbDistance() {
    return this._dynamicAbsorbDistance;
  }

  private updateDynamicAbsorbDistance() {
    this._dynamicAbsorbDistance = DEFAULT_DYNAMIC_ABSORB_DISTANCE / normalizeZoom(this.currentSceneZoom);
  }

  get scene2dZoom() {
    return this._scene2dZoom;
  }

  set scene2dZoom(zoom: number) {
    if (this._scene2dZoom === zoom) return;

    this._scene2dZoom = zoom;
    this.updateDynamicAbsorbDistance();
    editorEvent.emit(EditorEventName.ZOOM_UPDATE_2D, zoom);
  }

  get scene3dZoom() {
    return this._scene3dZoom;
  }

  set scene3dZoom(zoom: number) {
    if (this._scene3dZoom === zoom) return;

    this._scene3dZoom = zoom;
    this.updateDynamicAbsorbDistance();
    editorEvent.emit(EditorEventName.ZOOM_UPDATE_3D, zoom);
  }

  set operateInfo(operateInfo: IOperateInfo) {
    if (isEqual(this._operateInfo, operateInfo)) return;
    const ignoreResetSelectedMode = [
      EOperateMode.EMPTY,
      EOperateMode.Alignment,
      EOperateMode.MultiSelect,
      EOperateMode.DrawMultiModel,
      EOperateMode.Array
    ];

    // 先进行2d/3d模式切换
    if (this._sceneMode === ESceneMode.scene3d) {
      const needToSwitchTo2d = MODES_ONLY_AVAILIABLE_IN_2D.includes(operateInfo.mode);

      if (needToSwitchTo2d) {
        this.sceneMode = ESceneMode.scene2d;
      }
    }

    // 如果操作模式不为空，则清空选中
    if (!ignoreResetSelectedMode.includes(operateInfo.mode)) {
      this.editorStateStore.setSelected({
        entityId: '',
        type: EEntityType.EMPTY
      });
    }

    // 如果当前处于对齐模式下，则取消对齐模式
    if (this._operateInfo.mode === EOperateMode.Alignment) {
      this.alignOperation.toggleAlignModeEnabled(false, true);
    }

    // 再进行操作模式切换
    this._prevOperateInfo = this._operateInfo;
    this._operateInfo = operateInfo;

    editorEvent.emit(EditorEventName.OPERATE_MODE_CHANGE, operateInfo);
  }

  get prevOperateInfo() {
    return this._prevOperateInfo;
  }

  get sceneMode() {
    return this._sceneMode;
  }

  set sceneMode(sceneMode: ESceneMode) {
    if (this._sceneMode === sceneMode) return;

    // 视图模式切换时，退出对齐
    if (this.operateInfo.mode === EOperateMode.Alignment) {
      this.alignOperation.toggleAlignModeEnabled(false);
    }

    // 模式切换时，清空hover信息
    this.editorStateStore.clearHoverEntity();

    // 模式切换时，有选择地清空选中
    if (
      this.editorStateStore.selected.entityId &&
      !ENTITY_SHOULD_KEEP_SELECT_WHEN_SCENE_MODE_CHANGE.includes(this.editorStateStore.selected.type)
    ) {
      this.editorStateStore.clearSelected();
    }

    this._sceneMode = sceneMode;
    this.updateDynamicAbsorbDistance();
    editorEvent.emit(EditorEventName.SCENE_MODE_CHANGE, sceneMode);
    if (sceneMode === ESceneMode.scene3d) {
      // 切换到 3D 时有选择地清除 operateInfo
      if (MODES_ONLY_AVAILIABLE_IN_2D.includes(this.operateInfo.mode)) {
        this.operateInfo = {
          mode: EOperateMode.EMPTY,
          toolType: EToolType.empty
        };
      }
    }
  }

  initScene2dInfo() {
    const size = 10; // m

    const scene = new Scene();
    const intersectableGroup = new Group();
    intersectableGroup.userData.containerType = EContainerType.intersectable;
    scene.add(intersectableGroup);

    const camera = new OrthographicCamera(-size, size, size, -size, 1, 600);

    const renderer = new WebGLRenderer({ antialias: true });
    renderer.outputColorSpace = SRGBColorSpace;
    renderer.setClearColor(sceneBgColor, 1); // 设置背景颜色
    renderer.setPixelRatio(window.devicePixelRatio); // 设置像素比

    camera.position.set(0, 0, 500); // 设置相机位置
    camera.lookAt(0, 0, 0); // 设置相机朝向
    camera.layers.enableAll();

    return {
      renderer,
      scene,
      intersectableGroup,
      camera,
      canvasElement: renderer.domElement
    };
  }

  initScene3dInfo() {
    const scene = new Scene();
    const intersectableGroup = new Group();
    intersectableGroup.name = 'intersectableContainer';
    intersectableGroup.userData.intersectableContainer = true;
    scene.add(intersectableGroup);

    const camera = new PerspectiveCamera(75, 1, 0.1, 4000);
    camera.up.set(0, 0, 1);
    camera.position.set(0, -10, 10);

    const renderer = new WebGLRenderer({ antialias: true });
    renderer.outputColorSpace = SRGBColorSpace;
    renderer.setPixelRatio(window.devicePixelRatio); // 设置像素比

    scene.background = new Color(sceneBgColor);
    scene.fog = new Fog(scene.background, 1, 500);

    // 全局光照
    scene.add(new AmbientLight(0xffffff, 0.3));
    {
      // 环境光设置
      const envTexture = new TextureLoader().load(roomEnvImage);
      envTexture.mapping = EquirectangularReflectionMapping;
      envTexture.colorSpace = SRGBColorSpace;
      // scene.background = envTexture;
      // scene.backgroundRotation.x = Math.PI / 2;
      scene.environment = envTexture;
      scene.environmentRotation.x = Math.PI / 2;
      scene.environmentIntensity = 3.5;
    }

    const vertexShader = `
      varying vec3 vWorldPosition;
			void main() {
				vec4 worldPosition = modelMatrix * vec4( position, 1.0 );
				vWorldPosition = worldPosition.xyz;
				gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );
			}`;
    const fragmentShader = `
      uniform vec3 topColor;
			uniform vec3 bottomColor;
			uniform float offset;
			uniform float exponent;
			varying vec3 vWorldPosition;

			void main() {
				float h = normalize( vWorldPosition + offset ).z;
				gl_FragColor = vec4( mix( bottomColor, topColor, max( pow( max( h , 0.0), exponent ), 0.0 ) ), 1.0 );

			}`;
    const uniforms = {
      topColor: { value: new Color(0xcfeeff) },
      bottomColor: { value: new Color(0xfefefe) },
      offset: { value: 0 },
      exponent: { value: 1.5 }
    };
    // uniforms['topColor'].value.copy(hemiLight.color);
    scene.fog.color.copy(uniforms['bottomColor'].value);
    const skyGeo = new SphereGeometry(1000, 32, 15);
    const skyMat = new ShaderMaterial({
      uniforms: uniforms,
      vertexShader: vertexShader,
      fragmentShader: fragmentShader,
      side: BackSide
    });
    const sky = new Mesh(skyGeo, skyMat);
    scene.add(sky);

    return {
      renderer,
      scene,
      intersectableGroup,
      camera,
      canvasElement: renderer.domElement
    };
  }

  // 根据entityId和type获取数据
  public getDataByIdType<T extends EEntityType>(entityId: string, type: T): TEntityTypeDataMap[T] | undefined {
    if (!type) return;

    if (type !== EEntityType.MultiModel && !entityId) return;

    const dataFetchers: { [key in EEntityType]?: (id: string) => TEntityTypeDataMap[key] | undefined } = {
      [EEntityType.Wall]: (id) => this._wallStore.getWallData(id),
      [EEntityType.SeparateWall]: (id) => this._separateWallStore.getSepData(id, false),
      [EEntityType.Floor]: (id) => this._roomStore.getRoomData(id),
      [EEntityType.DoorWindow]: (id) => this._modelStore.getWindowData(id),
      [EEntityType.Column]: (id) => this._structStore.getColumnData(id),
      [EEntityType.Furniture]: (id) => this._modelStore.getFurnitureData(id),
      [EEntityType.Region]: (id) => this.regionStore.getRegionData(id),
      [EEntityType.Face]: (id) => this.faceStore.getFaceData(id),
      [EEntityType.ModelGroup]: (id) => this._modelGroupStore.getModelGroupData(id),
      [EEntityType.Assembly]: (id) => this._assemblyStore.getAssemblyData(id),
      [EEntityType.Room]: (id) => this.roomStore.getRoomData(id),
      [EEntityType.AreaDimension]: (id) => this.dimensionStore.getAreaDim(id),
      [EEntityType.MultiModel]: (_id) => {
        const multiModelData = this.editorStateStore.multiSelectTemporary;
        if (!multiModelData) return undefined;
        // 空多选不算有效数据：refreshFurnitureMultiSelected 会把已删构件从 modelInfos 里剔除，
        // 剔空后若还返回它，selectedRerender 的兜底就清不掉这个空的多选选中（多选删除后面板不复位即出自此）
        if (!multiModelData.modelInfos.length) return undefined;
        if (multiModelData.modelInfos.some((info) => !this.getDataByIdType(info.id, info.type))) return undefined;
        return multiModelData;
      },
      [EEntityType.LinearDimension]: (id) => this.dimensionStore.getLinearDim(id)
    };

    return dataFetchers[type]?.(entityId);
  }

  // 检测当前seleted对应的数据是否还在
  checkEntityThenRefreshSelected() {
    const selected = this.editorStateStore.selected;
    if (!selected.entityId) return;

    const entityData = this.getDataByIdType(selected.entityId, selected.type);
    if (!entityData) {
      this.editorStateStore.clearSelected();
    }
  }

  getDefaultMaterial(
    params: {
      type?: EFaceType;
      sepWallData?: Pick<SeparateWallData, 'type'>;
      layout?: EWallFaceLayout;
    } = {}
  ): IMaterialInfo {
    switch (params.type) {
      case EFaceType.SepWallFace: {
        const isGlassFace =
          params.sepWallData?.type === ESeparateWallType.GlassWall ||
          (params.sepWallData?.type === ESeparateWallType.HalfGlassWall && params.layout === EWallFaceLayout.MIDDLE);

        return isGlassFace
          ? {
              id: globalStores.materialStore.getDefaultMaterial(EFaceType.SepWallFace)?.id ?? ''
            }
          : {
              id: globalStores.materialStore.getDefaultMaterial(EFaceType.WallFace)?.id ?? ''
            };
      }
      case EFaceType.FloorFace:
        return {
          id: globalStores.materialStore.getDefaultMaterial(params.type)?.id ?? ''
        };
      default:
        return {
          id: globalStores.materialStore.getDefaultMaterial(params.type)?.id ?? ''
        };
    }
  }

  // ======================== 墙体相关 ========================
  // 更新墙体(包含门窗)
  updateWallData(wallId: string, wallBaseData: Partial<WallBaseData>) {
    this._wallStore.updateWallBaseData(wallId, wallBaseData);
    const wallData = this._wallStore.getWallData(wallId);
    if (!wallData) return;

    this.updateDoorWindowData(wallId);
  }

  updateDoorWindowData(relativeWallId: string) {
    const relativeDoorWindowData = this._modelStore
      .getWindowDatas()
      .filter((windowData) => windowData.relativeWallInfo.relativeWallId === relativeWallId);

    relativeDoorWindowData.forEach((windowData) => {
      // 进行一次门窗数据更新
      const newWindowData = syncDistanceTypeToCenterType(windowData);
      this._modelStore.updateWindowData(windowData.id, newWindowData);
    });
  }

  // ======================== 房间/区域/墙面/连接点计算 ========================
  // 墙体变更后，刷新数据
  refreshData() {
    // 计算壁芯
    const coreAreaResult = this._refreshCorePathInfos();

    // 计算区域轮廓
    const { remainingEdges, connectedWallOutlines, roomAreaResult, roomCoreArea } =
      this._refreshPointsAndOutline(coreAreaResult);

    // 更新墙体边数据
    this.edgeStore.updateWallEdges(
      remainingEdges.concat(
        connectedWallOutlines.filter((outline) => !outline.belongArea).flatMap((outline) => outline.edges)
      )
    );

    // 更新房间数据
    this._roomStore.refreshRoomDatas(
      roomAreaResult.map((info, i) => ({
        pathInfos: info.edges,
        holeInfos: info.holesEdges,
        corePathInfos: roomCoreArea[i].edges,
        coreHoleInfos: roomCoreArea[i].holesEdges
      }))
    );

    this.connectedWallOutlines = connectedWallOutlines;
    this.remainingEdges = remainingEdges;

    // 根据房间内轮廓，更新基础隔墙数据
    this._separateWallStore.refreshBaseSeparateWallDatas();
    // 更新所有隔墙形状
    this._separateWallStore.updateAllSeparateWallShape();

    // 更新区域数据
    this._regionStore.refreshRegionDatas();

    // 更新墙面数据
    this._faceStore.refreshFaceDatas();

    // 删除旧房间点/边数据
    this.pointStore.refreshRoomPoints();
    this.edgeStore.refreshSepEdges();

    // console.log('refreshData');
    // console.log('pointDatas: ', this.pointStore.getData());
    // console.log('faceDatas: ', this.faceStore.getWallFaceDatas());
  }

  refreshByRoomId(roomId: string) {
    this._regionStore.refreshRegionDatasByRoomId(roomId);
    this._faceStore.refreshFaceDatas();
  }

  private _refreshCorePathInfos() {
    const wallDatas = this._wallStore.getWallDatas();
    const segs: IShapeSeg2d[] = [];
    const segsInfos: IEdgeInfo[] = [];

    for (const wallData of wallDatas) {
      const start = wallData.baseData.start;
      const end = wallData.baseData.end;
      const center = wallData.baseData.center;
      const clockwise = wallData.baseData.clockwise;

      // ==== 弧墙处理 ====
      if (center && clockwise !== undefined) {
        const arc = new ArcSeg2d({ start, end, center, clockwise: clockwise });
        segs.push(arc);
        segs.push(arc.getOppositeArc());
        segsInfos.push({ wallId: wallData.id, start, end, side: EFaceSide.MainLeftSide, startId: 0, endId: 0 });
        segsInfos.push({ wallId: wallData.id, start, end, side: EFaceSide.MainRightSide, startId: 0, endId: 0 });
        continue;
      }

      // ==== 直墙处理 ====
      segs.push(new LineSeg2d(start, end));
      segs.push(new LineSeg2d(end, start));
      segsInfos.push({ wallId: wallData.id, start, end, side: EFaceSide.MainLeftSide, startId: 0, endId: 0 });
      segsInfos.push({ wallId: wallData.id, start, end, side: EFaceSide.MainRightSide, startId: 0, endId: 0 });
    }

    const coreAreaFinder = new PathFinder({
      segments: segs,
      segRelativeInfos: segsInfos,
      isDirectedSegments: true,
      needClearFreeEdges: true,
      preserveOppositeEdge: true
    });

    const { areaResult } = coreAreaFinder.getArea();

    return areaResult;
  }

  private _refreshPointsAndOutline(coreAreaResult: IAreaResult<IWallEdge>[]) {
    const wallDatas = this._wallStore.getWallDatas();

    // 获取所有的墙体轮廓
    const segs: IShapeSeg2d[] = [];
    const relativeInfos: IEdgeWallSideInfoWithRelative[] = [];
    for (const wallData of wallDatas) {
      const { segs: wallSegs, relativeInfos: wallRelativeInfos } = getWallDrawShapeInfos(wallData);
      segs.push(...wallSegs);
      relativeInfos.push(...wallRelativeInfos);
    }

    // 获取所有的区域轮廓
    const closedRoomFinder = new PathFinder({
      segments: segs,
      segRelativeInfos: relativeInfos
    });
    const { areaResult, outerAreas, remainingEdges: _remainingEdges } = closedRoomFinder.getArea();

    // 有效区域检验
    const { validCoreArea, validOutlineArea, invalidOutlineArea } = compareCoreAndOutline(coreAreaResult, areaResult);

    // edge提取
    const edges: Edge<IEdgeWallSideInfoWithRelative>[] = [];
    validOutlineArea.forEach((area) => {
      edges.push(...area.edges);
      area.holesEdges.forEach((holeEdges) => edges.push(...holeEdges));
    });
    invalidOutlineArea.forEach((area) => {
      edges.push(...area.edges);
      area.holesEdges.forEach((holeEdges) => edges.push(...holeEdges));
    });
    outerAreas.forEach((area) => edges.push(...area.edges));
    edges.push(..._remainingEdges);
    // const removePointIds = new Set(this.pointStore.getWallPointIds());
    const newWallPointSet = new Set<number>();

    // 获取所有的连接点Node
    for (const edge of edges) {
      // 端点必然是 closedRoomFinder 点树里的节点；取不到就跳过，不给这条 edge 打上无效的 startId/endId
      const fromNode = closedRoomFinder.getNode(edge.from);
      const toNode = closedRoomFinder.getNode(edge.to);
      if (!fromNode || !toNode) continue;

      const fromPointHash = genStrsHash(getWallSideStrArr(fromNode.infos));
      const toPointHash = genStrsHash(getWallSideStrArr(toNode.infos));

      // 更新rtree 节点的id
      fromNode.id = fromPointHash;
      toNode.id = toPointHash;

      edge.info = {
        ...edge.info,
        startId: fromPointHash,
        endId: toPointHash
      };

      newWallPointSet.add(fromPointHash);
      newWallPointSet.add(toPointHash);
    }

    // 更新墙体点集合
    this.pointStore.refreshWallSet(newWallPointSet);

    // 获取墙体轮廓
    const connectedWallOutlines = outerAreas.map((area) => ({
      path: area.path,
      edges: area.edges.map(edgeToWallEdge),
      outest: true,
      belongArea: false
    }));
    invalidOutlineArea.forEach((area) => {
      connectedWallOutlines.push({
        path: area.path,
        edges: area.edges.map((edge) => edgeToWallEdge(edge)),
        outest: false,
        belongArea: false
      });

      for (let i = 0; i < area.holes.length; i++) {
        connectedWallOutlines.push({
          path: area.holes[i],
          edges: area.holesEdges[i].map((edge) => edgeToWallEdge(edge)),
          outest: false,
          belongArea: false
        });
      }
    });
    validOutlineArea.forEach((area) => {
      connectedWallOutlines.push({
        path: area.path,
        edges: area.edges.map((edge) => edgeToWallEdge(edge)),
        outest: false,
        belongArea: true
      });

      for (let i = 0; i < area.holes.length; i++) {
        connectedWallOutlines.push({
          path: area.holes[i],
          edges: area.holesEdges[i].map((edge) => edgeToWallEdge(edge)),
          outest: false,
          belongArea: true
        });
      }
    });

    // 房间的区域结果
    const roomAreaResult = validOutlineArea.map((area) => {
      const edges = area.edges.map((edge) => edgeToWallEdge(edge));
      const holesEdges = area.holesEdges.map((holesEdges) => holesEdges.map((edge) => edgeToWallEdge(edge)));

      return {
        // path: area.path,
        edges,
        // holes: area.holes,
        holesEdges,
        parent: null,
        children: []
      };
    });

    // 剩余的边
    const remainingEdges = _remainingEdges.map((edge) => edgeToWallEdge(edge));

    return {
      remainingEdges,
      connectedWallOutlines,
      roomAreaResult,
      roomCoreArea: validCoreArea.map((area) => ({
        edges: area.edges.map((edge) => edgeToWallEdge(edge)),
        holesEdges: area.holesEdges.map((holesEdges) => holesEdges.map((edge) => edgeToWallEdge(edge)))
      }))
    };
  }

  // ======================== 其他 ========================
  clearAll() {
    this.connectedWallOutlines = [];
    this.remainingEdges = [];

    this.structStore.clearAll();
    this.modelStore.clearAll();
    this.assemblyStore.clearAll();
    this.regionStore.clearAll();
    this.separateWallStore.clearAll();
    this.roomStore.clearAll();
    this.wallStore.clearAll();
    this.faceStore.clearAll();
    this.pointStore.clearAll();
    this.edgeStore.clearAll();
    this.dimensionStore.clearAll();
  }
}

export const globalStores = new Stores();

export type TGlobalStores = typeof globalStores;

function getWallDrawShapeInfos(wallData: WallData) {
  const startShape = wallData.drawWallInfo.startShape;
  const endShape = wallData.drawWallInfo.endShape;
  const center = wallData.baseData.center;
  const clockwise = wallData.baseData.clockwise || false;

  const segs: IShapeSeg2d[] = [];
  const relativeInfos: IEdgeWallSideInfoWithRelative[] = [];

  if (center) {
    // 弧墙处理
    const centerArc = new ArcSeg2d({ start: wallData.baseData.start, end: wallData.baseData.end, center, clockwise });

    // 左边线
    const leftSeg = new ArcSeg2d({ start: startShape[0], end: endShape[0], center: centerArc.center, clockwise });
    segs.push(leftSeg);
    relativeInfos.push({ wallId: wallData.id, side: EFaceSide.MainLeftSide, startId: 0, endId: 0 });

    // 右边线
    const rightSeg = new ArcSeg2d({
      start: endShape[endShape.length - 1],
      end: startShape[startShape.length - 1],
      center: centerArc.center,
      clockwise: !clockwise
    });
    segs.push(rightSeg);
    relativeInfos.push({ wallId: wallData.id, side: EFaceSide.MainRightSide, startId: 0, endId: 0 });
  } else {
    // 左边线
    const leftSeg = new LineSeg2d(startShape[0], endShape[0]);
    segs.push(leftSeg);
    relativeInfos.push({ wallId: wallData.id, side: EFaceSide.MainLeftSide, startId: 0, endId: 0 });

    // 右边线
    const rightSeg = new LineSeg2d(endShape[endShape.length - 1], startShape[startShape.length - 1]);
    segs.push(rightSeg);
    relativeInfos.push({ wallId: wallData.id, side: EFaceSide.MainRightSide, startId: 0, endId: 0 });
  }

  // 起点边线
  const reverseStartShape = reverseShape(startShape);
  for (let i = 0; i < reverseStartShape.length - 1; i++) {
    segs.push(getShapeSeg({ start: reverseStartShape[i], end: reverseStartShape[i + 1], ...reverseStartShape[i] }));
    relativeInfos.push({ wallId: wallData.id, side: EFaceSide.StartSide, startId: 0, endId: 0 });
  }

  // 终点边线
  for (let i = 0; i < endShape.length - 1; i++) {
    segs.push(getShapeSeg({ start: endShape[i], end: endShape[i + 1], ...endShape[i] }));
    relativeInfos.push({ wallId: wallData.id, side: EFaceSide.EndSide, startId: 0, endId: 0 });
  }

  return {
    segs,
    relativeInfos
  };
}
